import type { BlogPost } from '../../../../app/features/blog/blog.types';

type RawPost = Omit<BlogPost, 'readingTimeMinutes'>;

export const designArchPosts: RawPost[] = [
  {
    slug: 'design-tokens-cascade',
    title: 'Design Tokens: un cambio, sesenta componentes actualizados',
    description:
      'Cómo CSS custom properties y tokens SCSS propagan cambios a escala de sistema. Un ajuste en un archivo, toda la librería actualizada.',
    date: '2026-04-14',
    content: `## El problema

En un sistema de diseño con sesenta componentes, cambiar el color primario de la marca implica buscar y reemplazar valores hexadecimales en decenas de archivos. Cada reemplazo es una oportunidad de error y una regresión visual silenciosa.

La causa raíz no es la cantidad de componentes: es la ausencia de una única fuente de verdad para los valores de diseño.

## La solución

Los Design Tokens son variables con nombre que representan decisiones de diseño: colores, tipografía, espaciado, radios de borde. La implementación más directa combina SCSS como capa de definición con CSS custom properties como capa de distribución.

\`\`\`css
/* tokens.css — única fuente de verdad */
:root {
  --color-primary-500: #6366f1;
  --color-primary-600: #4f46e5;
  --spacing-base: 0.25rem;
  --radius-md: 0.375rem;
  --font-sans: 'Inter', system-ui, sans-serif;
}
\`\`\`

Los componentes referencian tokens, nunca valores literales:

\`\`\`scss
// button.component.scss
.btn-primary {
  background: var(--color-primary-500);
  padding: calc(var(--spacing-base) * 2) calc(var(--spacing-base) * 4);
  border-radius: var(--radius-md);

  &:hover {
    background: var(--color-primary-600);
  }
}
\`\`\`

Cambiar \`--color-primary-500\` en \`tokens.css\` actualiza cada botón, cada badge, cada enlace activo del sistema. Sin tocar un solo componente.

<iframe src="https://stackblitz.com/edit/design-tokens-cascade?embed=1&file=src/styles/tokens.css" width="100%" height="500" title="Design Tokens"></iframe>

## Puntos clave

- Un token es una decisión de diseño nombrada, no un valor arbitrario
- CSS custom properties permiten sobreescritura en cualquier nivel del cascade
- Los temas (dark mode, marca blanca) se implementan redefiniendo tokens en un selector raíz

## Cuándo aplicar este patrón

Desde el primer componente. El coste de introducir tokens en un sistema existente crece linealmente con el número de componentes. En proyectos multi-marca o con modo oscuro, los tokens son un requisito, no una optimización.
`,
  },
  {
    slug: 'state-management-comparison',
    title: 'Redux vs Signals vs BehaviorSubject: tres caminos, una decisión',
    description:
      'Implementación del mismo carrito de compra con tres enfoques. Criterios concretos para elegir la complejidad adecuada al problema.',
    date: '2026-04-14',
    content: `## El problema

La gestión de estado es la decisión de arquitectura que más frecuentemente se sobredimensiona. Aplicar NgRx a un formulario de cinco campos, o usar un \`BehaviorSubject\` suelto en una aplicación con doce módulos, produce el mismo resultado: código difícil de mantener por razones opuestas.

La elección correcta depende de tres variables: número de consumidores del estado, frecuencia de mutaciones, y necesidad de trazabilidad.

## La solución

El mismo carrito de compra, tres implementaciones:

**BehaviorSubject** — para estado local con pocos consumidores:

\`\`\`typescript
private readonly _items = new BehaviorSubject<CartItem[]>([]);
readonly items$ = this._items.asObservable();

addItem(item: CartItem): void {
  this._items.next([...this._items.value, item]);
}
\`\`\`

**Signal** — para estado de componente o feature aislada:

\`\`\`typescript
readonly items = signal<CartItem[]>([]);
readonly total = computed(() => this.items().reduce((sum, i) => sum + i.price, 0));

addItem(item: CartItem): void {
  this.items.update(prev => [...prev, item]);
}
\`\`\`

**NgRx Store** — para estado global con múltiples features y devtools:

\`\`\`typescript
// cart.actions.ts
export const addItem = createAction('[Cart] Add Item', props<{ item: CartItem }>());

// cart.reducer.ts
on(addItem, (state, { item }) => ({ ...state, items: [...state.items, item] }))
\`\`\`

<iframe src="https://stackblitz.com/edit/angular-state-management-comparison?embed=1&file=src/app/cart/cart.store.ts" width="100%" height="500" title="State Management Comparison"></iframe>

## Puntos clave

- BehaviorSubject es suficiente para servicios singleton con un consumidor primario
- Signals eliminan la suscripción explícita y el riesgo de memory leaks en componentes
- NgRx aporta valor cuando el historial de mutaciones, el time-travel debugging o la coordinación entre múltiples stores son requisitos reales

## Cuándo aplicar este patrón

Empezar con Signals o BehaviorSubject. Migrar a NgRx cuando aparezca un requisito concreto: auditoría de mutaciones, replay de acciones, estado compartido entre rutas lazy-loaded que no comparten un ancestro común.
`,
  },
];
